import d3 from './d3-imports';
import formatShortDate from './format-date';
import NumberFormatter from './numberFormatter';

const textConfigs = {
    default: {
        mobile: {
            textAnchor: 'middle',
            textTranslate: -20,
            dateTranslate: 0,
        },
        desktop: {
            textAnchor: 'middle',
            textTranslate: 0,
            dateTranslate: 15,
        },
    },
    rightAligned: {
        mobile: {
            textAnchor: 'end',
            textTranslate: 15,
            dateTranslate: 0,
        },
        desktop: {
            textAnchor: 'end',
            textTranslate: 15,
            dateTranslate: 15,
        },
    },
};

const languageConfigs = {
    mundo: textConfigs.rightAligned,
    portuguese: textConfigs.rightAligned,
    russian: textConfigs.rightAligned,
};

const languagesWithoutCountdown = [
    'japanese',
    'korean',
];


export default class Chart {
    constructor(data, options = {}) {
        this.initOptions(options);
        this.data = this.normaliseData(data);

        this.margin = this.isMobile() ? {
            top: 60, right: 0, bottom: 30, left: 25,
        } : {
            top: 60, right: 5, bottom: 30, left: 25,
        };

        this.updateDimensions();
        this.init();
    }

    initOptions(options) {
        this.language = options.language || 'news';
        this.locale = options.locale || 'en-GB';
        this.scope = options.scope || document;
        this.fixedWidth = options.fixedWidth || 0;
        this.fixedHeight = options.fixedHeight || 0;
        this.coreContent = options.coreContent || false;
        this.root = options.root || this.scope.querySelector('.chart');
        this.chartContainer = d3.select(this.root).select('.chart-container');

        if (options.vocabs) {
            // Overriding vocabs translation function for server-side
            this.vocabs = options.vocabs;
            global['__'] = (key) => this.vocabs[key];
        }
    }

    init() {
        this.addSvg();
        this.setupAxes();
        this.buildChart();
        this.render();
        if (!this.coreContent) {
            this.removeViewBox();
            this.setupEvents();
            this.resize();
        }
    }

    isMobile() {
        const width = this.fixedWidth || window.innerWidth;
        return width <= 480;
    }

    languageConfig() {
        const config = languageConfigs[this.language] || textConfigs.default;
        return this.isMobile() ? config.mobile : config.desktop;
    }

    removeViewBox() {
        // Viewbox interferes with JS-drive resizing
        this.svgElement.attr('viewBox', null);
    }

    normaliseData(data) {
        return data.map(r => {
            return {
                ...r,
                value: Number(r.value),
            };
        });
    }

    updateDimensions() {
        if (this.coreContent) {
            this.width = this.fixedWidth - this.margin.left - this.margin.right;
            this.height = this.fixedHeight - this.margin.top - this.margin.bottom;
        }
        else {
            const availableWidth = this.root.clientWidth - 5;
            this.width = availableWidth - this.margin.left - this.margin.right;
            this.height = 380 - this.margin.top - this.margin.bottom;
        }
    }

    addSvg() {
        if (this.coreContent) {
            const { width, height } = this.calculateSvgSize();
            const svg = this.chartContainer.append('svg')
                .attr('xmlns', 'http://www.w3.org/2000/svg')
                .attr('class', 'poll-chart')
                .attr('aria-hidden', true);

            if (this.coreContent) {
                svg.attr('viewBox', `0 0 ${this.fixedWidth} ${this.fixedHeight}`);
            }
            else {
                svg.attr('width', width)
                    .attr('height', height);
            }

            this.svgElement = svg;
        }
        else {
            this.svgElement = this.chartContainer.select('svg');
        }
    }

    resizeSvg() {
        const { width, height } = this.calculateSvgSize();

        this.svgElement.attr('width', width).attr('height', height);
    }

    calculateSvgSize() {
        const size = {
            width: this.width + this.margin.left + this.margin.right,
            height: this.height + this.margin.top + this.margin.bottom,
        };
        return size;
    }

    rangeX() {
        return [5, this.width - 5];
    }

    rangeY() {
        return [this.height, 0];
    }

    updateRanges() {
        this.x.range(this.rangeX());
        this.y.range(this.rangeY());
    }

    getCountdownString(electionDay) {
        if (languagesWithoutCountdown.indexOf(this.language) !== -1) {
            return '';
        }

        // Number of milliseconds in a day:
        const day = 60 * 60 * 24 * 1000;
        const dateDifferenceTime = electionDay[0].getTime() - Date.now();
        const dateDifferenceDays = Math.ceil(dateDifferenceTime / day);

        if (dateDifferenceDays < 1) return '';

        const formattedDays = NumberFormatter.format(this.language, dateDifferenceDays);
        let countdown = `${formattedDays} ${__('days-until')}`;

        if (dateDifferenceDays === 1) {
            countdown = `${formattedDays} ${__('days-until-singular')}`;
        }

        return countdown;
    }

    setupAxes() {
        const parser = d3.timeParse('%Y-%m-%d');
        // This is the first and last date in the time series data:
        const timeseriesExtent = d3.extent(this.data, d => d.date);
        const latestDate = timeseriesExtent[1];
        const startMonth = (parser(timeseriesExtent[0]).getMonth() + 1 < 10) ? `0${parser(timeseriesExtent[0]).getMonth() + 1}` : `${parser(timeseriesExtent[0]).getMonth() + 1}`;
        // Election day is 3rd Nov so make the end date mid nov
        timeseriesExtent[1] = '2020-11-30';
        timeseriesExtent[0] = `2020-${startMonth}-01`;
        const startXAxis = parser(timeseriesExtent[0]);
        const endXAxis = parser(timeseriesExtent[1]);
        // the month names will be in the middle of each month on the x axis
        const xTickValues = [];
        for (let i = startMonth; i < 12; i++) {
            xTickValues.push(`2020-${i}-15`);
        }
        // the tick lines will be at the start of each month
        const xTickLines = [];
        for (let i = startMonth; i < 12; i++) {
            xTickLines.push(`2020-${i}-01`);
        }
        const electionDay = '2020-11-03';
        const parsedxTick = [];
        const parsedxLine = [];
        const parsedElectionDay = [parser(electionDay)];
        const parsedLatestDate = [parser(latestDate)];
        // the endVals are the latest polling values
        const endVals = [];
        let trumpTotal;
        let bidenTotal;
        for (let i = 0; i < this.data.length; i++) {
            if (this.data[i].date === latestDate) {
                endVals.push(this.data[i].median);
                if (this.data[i].name === 'Trump') {
                    trumpTotal = Number(this.data[i].median);
                }
                else {
                    bidenTotal = Number(this.data[i].median);
                }
            }
        }
        const maxEndVal = Math.max(...endVals);
        xTickValues.forEach((i) => {
            parsedxTick.push(parser(i));
        });
        xTickLines.forEach((i) => {
            parsedxLine.push(parser(i));
        });

        const x = d3.scaleTime()
            .domain([startXAxis, endXAxis])
            .range(this.rangeX());
        // The y-axis needs to accommodate the largest data point in the data
        const largestDataPoint = d3.max(this.data, d => d.value);
        const smallestDataPoint = d3.min(this.data, d => d.value);

        // Define the top of the y-axis by rounding up from this number to the nearest 10:
        const topOfTheChart = (largestDataPoint < 60) ? 65 : largestDataPoint + 10 - (largestDataPoint % 10);
        const bottomOfTheChart = (smallestDataPoint > 30) ? 30 : smallestDataPoint - (smallestDataPoint % 10);
        const y = d3.scaleLinear()
            .domain([bottomOfTheChart, topOfTheChart])
            .range(this.rangeY());
        // Manually define the tick values as increments of 10 from zero to the topOfTheChart:
        const yTickValues = [];
        for (let i = bottomOfTheChart; i <= topOfTheChart; i += 10) {
            yTickValues.push(i);
        }

        const months = [
            'jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', '', '', '',
        ].map(m => (m ? `${m}-initial` : ''));

        this.xTickValues = parsedxTick;
        this.yTickValues = yTickValues;
        this.xTickLines = parsedxLine;
        this.parsedElectionDay = parsedElectionDay;
        this.electionDayText = `${__('election-day')}`;
        this.electionDayCountdown = this.getCountdownString(parsedElectionDay);
        this.parsedLatestDate = parsedLatestDate;
        this.endVal = maxEndVal;
        this.endVals = endVals;
        this.parser = parser;
        this.latestDate = latestDate;
        this.topOfTheChart = topOfTheChart;
        this.bottomOfTheChart = bottomOfTheChart;
        this.trumpTotal = trumpTotal;
        this.bidenTotal = bidenTotal;
        this.months = months;
        this.candidates = this.parseData();

        this.x = x;
        this.y = y;

        const totals = {
            trump: this.trumpTotal,
            biden: this.bidenTotal,
        };
    }

    drawXAxis(svg) {
        const tickFormatHandler = d => {
            const vocab = this.isMobile() ?
                this.months[d.getMonth()] :
                d3.timeFormat('%b')(d).toLowerCase();
            return __(vocab);
        };

        // Display x-axis ticks on charts:
        this.selectOrCreateTag('g', 'axis-x', svg)
            .attr('transform', `translate(5, ${this.height + 5})`)
            .call(d3.axisBottom(this.x)
                // .tickFormat(d3.timeFormat('%b'))
                .tickFormat(tickFormatHandler)
                .tickValues(this.xTickValues)
                .tickSize(0)
                .tickPadding(8))
            .call(g => g.select('.domain')
                .remove());
        this.selectOrCreateTag('g', 'axis-x-tick', svg)
            .attr('transform', `translate(5, ${this.height})`)
            .call(d3.axisBottom(this.x)
                .tickFormat('')
                .tickValues(this.xTickLines)
                .tickSize(5)
                .tickPadding(8))
            .call(g => g.select('.domain')
                .remove());
    }

    drawYAxis(svg) {
        // Add the y-axis to the chart, using axisRight and the chart width to draw gridlines:
        this.selectOrCreateTag('g', 'axis-y', svg)
            .call(d3.axisRight(this.y)
                .tickValues(this.yTickValues)
                .tickSize(0.926 * this.width)
                .tickFormat(n => NumberFormatter.format(this.language, n)))
            .call(g => g.selectAll('.tick text')
                .attr('x', -4)
                .attr('text-anchor', 'end'))
            .call(g => g.select('.domain')
                .remove());
        this.selectOrCreateTag('g', 'axis-y-bottom', svg)
            .call(d3.axisRight(this.y)
                .tickFormat('')
                .tickValues(this.yTickValues)
                .tickSize((this.width + this.margin.right)))
            .call(g => g.select('.domain')
                .remove());
    }

    drawElectionMarkerLine(svg) {
        const { textAnchor, textTranslate, dateTranslate } = this.languageConfig();

        this.selectOrCreateTag('g', 'election-day-line', svg)
            .attr('transform', 'translate(5, -10)')
            .attr('marker-start', 'url(#arrow)')
            .call(d3.axisBottom(this.x)
                .tickFormat((formatShortDate(this.parsedElectionDay, this.locale)))
                .tickValues(this.parsedElectionDay)
                .tickSize(this.height + 15)
                .tickPadding(8))
            .call(g => g.selectAll('.tick text')
                .attr('transform', `translate(${dateTranslate}, 0)`))
            .call(g => g.select('.domain')
                .remove());
        this.selectOrCreateTag('g', 'election-day-text', svg)
            .attr('transform', `translate(${textTranslate}, -35)`)
            .call(d3.axisBottom(this.x)
                .tickFormat((d) => this.electionDayText)
                .tickValues(this.parsedElectionDay)
                .tickSize(0)
                .tickPadding(8))
            .call(g => g.select('.domain')
                .remove())
            .attr('text-anchor', textAnchor);
        this.selectOrCreateTag('g', 'election-day-countdown', svg)
            .attr('transform', `translate(${textTranslate}, -55)`)
            .call(d3.axisBottom(this.x)
                .tickFormat((d) => this.electionDayCountdown)
                .tickValues(this.parsedElectionDay)
                .tickSize(0)
                .tickPadding(8))
            .call(g => g.select('.domain')
                .remove())
            .attr('text-anchor', textAnchor);
    }

    drawTrendMarkerLine(svg) {
        const endTrendVal = this.endVal;
        const transformTrend = this.height - 56 - (((endTrendVal + Math.abs(this.excess)) - this.bottomOfTheChart) * (this.height / (this.topOfTheChart - this.bottomOfTheChart)));
        const transformTrendDate = transformTrend + 35;

        const lineGroup = this.selectOrCreateTag('g', 'recent-day-line', svg);

        this.selectOrCreateTag('rect', 'trend-text-background', lineGroup)
            .attr('x', 4 + this.x(new Date(this.parsedLatestDate)))
            .attr('y', transformTrend + 2)
            .attr('width', 50)
            .attr('height', 38)
            .attr('fill', 'white');

        this.selectOrCreateTag('text', 'trend-date', lineGroup)
            .text(formatShortDate(new Date(this.parsedLatestDate), this.locale))
            .attr('x', 8 + this.x(new Date(this.parsedLatestDate)))
            .attr('y', transformTrendDate);

        this.selectOrCreateTag('line', 'trend-line', lineGroup)
            .attr('x1', this.x(new Date(this.parsedLatestDate)))
            .attr('x2', this.x(new Date(this.parsedLatestDate)))
            .attr('y1', 41.5)
            .attr('y2', this.y(this.y.domain()[0]) - 0.5);

        this.selectOrCreateTag('text', 'trend-text', lineGroup)
            .text(`${__('trend')}`)
            .attr('x', 8 + this.x(new Date(this.parsedLatestDate)))
            .attr('y', transformTrend + 8 * 2.2);
    }

    createMarkers(svg) {
        const marker = this.selectOrCreateTag('marker', 'election-arrow', svg)
            .attr('id', 'arrow')
            .attr('viewBox', [0, 0, 20, 20])
            .attr('refX', 10)
            .attr('refY', 10)
            .attr('markerWidth', 10)
            .attr('markerHeight', 10)
            .attr('orient', 'auto-start-reverse');
        this.selectOrCreateTag('path', 'election-arrow-path', marker)
            .attr('d', d3.line()([[0, 5], [10, 10], [0, 15], [10, 10], [0, 5]]))
            .attr('stroke', 'black');
        this.selectOrCreateTag('path', 'election-arrow-path-ie', marker)
            .attr('d', d3.line()([[0, 20], [10, 10], [20, 20], [10, 10], [0, 20]]))
            .attr('stroke', 'black');
    }

    parseData() {
        // Breaking up the data for each candidate, this can be moved somewhere else
        const candidates = {
            Biden: {
                data: this.data.filter(d => d.name === 'Biden'), // We only want Biden data
                colour: '#0a6aa6',
                total: Math.round(this.bidenTotal),
                transform: -1,
            },
            Trump: {
                data: this.data.filter(d => d.name === 'Trump'), // We only want Trump data
                colour: '#cd181c',
                total: Math.round(this.trumpTotal),
                transform: 1,
            },
        };

        let excess = 0;
        if (Math.abs(candidates['Trump'].total - candidates['Biden'].total) === 1) {
            excess = (candidates['Trump'].total - candidates['Biden'].total);
        }
        else if (candidates['Trump'].total === candidates['Biden'].total) {
            excess = -1;
        }

        this.excess = excess;
        return candidates;
    }

    drawChart(svg) {
        const avgLine = d3.line()
            .x(d => this.x(this.parser(d.date)))
            .y(d => this.y(d.median));

        // Render the leading candidate last so that their line shows on top
        const sortedCandidates = Object.keys(this.candidates).sort((a, b) => {
            return this.candidates[a].total - this.candidates[b].total;
        });

        // Creating one line per candidate
        sortedCandidates.forEach(candidate => {
            const pathClass = `avg-line average-line-${candidate.toLowerCase()}`;

            this.selectOrCreateTag('path', pathClass, svg)
                .datum(this.candidates[candidate].data) // Data for line gets injected here
                .attr('stroke', this.candidates[candidate].colour)
                .attr('d', d => avgLine(d))
                .attr('marker-start', `url(#dot-${candidate.toLowerCase()})`);

            const markerClass = `average-marker-${candidate.toLowerCase()}`;

            const marker = this.selectOrCreateTag('marker', markerClass, svg)
                .attr('class', 'dot-marker')
                .attr('id', `dot-${candidate.toLowerCase()}`)
                .attr('viewBox', [0, 0, 20, 20])
                .attr('refX', 10)
                .attr('refY', 10)
                .attr('markerWidth', 8)
                .attr('markerHeight', 8);


            this.selectOrCreateTag('circle', 'average-marker-circle', marker)
                .attr('cx', 10)
                .attr('cy', 10)
                .attr('r', 3)
                .attr('stroke', this.candidates[candidate].colour)
                .attr('stroke-width', '2.5')
                .attr('fill', 'white');

            const textClass = `average-figure-${candidate.toLowerCase()}`;

            const translate = (this.candidates[candidate].total + (this.candidates[candidate].transform * this.excess)) - this.bottomOfTheChart;
            const scale = this.height / (this.topOfTheChart - this.bottomOfTheChart);

            const textGroup = this.selectOrCreateTag('g', textClass, svg);
            this.selectOrCreateTag('text', 'average-figure-text', textGroup)
                .text(() => NumberFormatter.percentageFormat(this.language, this.candidates[candidate].total))
                .attr('x', 8 + this.x(new Date(this.parsedLatestDate)))
                .attr('y', this.height - (translate * scale) + 8)
                .style('fill', this.candidates[candidate].colour);

            this.selectOrCreateTag('rect', `percentage-${candidate.toLowerCase()}-background`, svg)
                .attr('x', 6 + this.x(new Date(this.parsedLatestDate)))
                .attr('y', this.height - (translate * scale) - 11)
                .attr('width', 40)
                .attr('height', 24)
                .attr('fill', 'white');

            this.scope.querySelector('.paths-group')
                .insertBefore(
                    this.scope.querySelector(`.percentage-${candidate.toLowerCase()}-background`),
                    this.scope.querySelector(`.average-figure-${candidate.toLowerCase()}`),
                );
        });
    }


    buildChart() {
        this.chartContainer
            .selectAll('.poll-chart')
            .data(this.data);

        this.svg = this.selectOrCreateTag('g', 'paths-group', this.svgElement)
            .attr('transform', `translate( ${this.margin.left} , ${this.margin.top} )`);
    }

    render() {
        this.drawXAxis(this.svg);
        this.drawYAxis(this.svg);
        this.createMarkers(this.svg);
        this.drawElectionMarkerLine(this.svg);
        this.drawTrendMarkerLine(this.svg);
        this.drawChart(this.svg);
        this.redrawIndividualPolls();
    }

    hideIndividualPolls() {
        if (this.pollsGroup) {
            this.pollsGroup.style('display', 'none');
        }
    }

    showIndividualPolls() {
        if (!this.pollsGroup) {
            this.renderIndividualPolls();
        }
        else {
            this.pollsGroup.style('display', 'inline');
        }
    }

    renderIndividualPolls() {
        if (!this.pollsGroup) {
            this.pollsGroup = this.selectOrCreateTag('g', 'individual-polls', this.svg);
        }

        this.pollsGroup.selectAll('.dot')
            .data(this.data)
            .enter()
            .append('circle')
            .attr('class', 'dot')
            .attr('cx', (d) => this.x(this.parser(d.date)))
            .attr('cy', (d) => this.y((d.value > 0 && d.value < 100) ? d.value : ''))
            .attr('r', 2)
            .attr('fill', (d) => this.candidates[`${d.name}`].colour)
            .attr('opacity', '50%');
    }

    redrawIndividualPolls() {
        if (this.pollsGroup) {
            this.pollsGroup.selectAll('circle')
                .attr('cx', (d) => this.x(this.parser(d.date)))
                .attr('cy', (d) => this.y((d.value > 0 && d.value < 100) ? d.value : ''));
        }
    }

    toggleIndividualPolls() {
        if (this.scope.querySelector('#polls-checkbox').checked) {
            this.showIndividualPolls();
        }
        else {
            this.hideIndividualPolls();
        }
    }

    selectOrCreateTag(tagName, className, container) {
        const selector = className.split(' ').map(c => `.${c}`).join('');

        let element = container.select(selector);
        if (!element.node()) {
            element = container.append(tagName)
                .attr('class', className);
        }
        return element;
    }

    resize() {
        this.updateDimensions();
        this.updateRanges();
        this.resizeSvg();
        this.render();
    }

    setupEvents() {
        d3.select(window)
            .on('resize.chart', this.resize.bind(this));

        this.scope.querySelector('.toggle')
            .addEventListener('change', this.toggleIndividualPolls.bind(this));
    }
}
